/**
 * SSC 3D chrome mark (snippets/ssc-chrome-logo.liquid).
 * Geometry, studio environment, material, camera and motion are ported from the approved
 * prototype (ssc-chrome-logo-reference.html): black chrome, "rest + turn".
 *
 * - three.js r128 + SVGLoader (theme assets) load after window load, when the browser is idle.
 * - The plain mark only shows as a fallback (no JS, no WebGL, load error, or not ready after 6s);
 *   otherwise the slot stays empty until the first frame, then the 3D logo fades in. The slot keeps
 *   its size, so there's no layout shift.
 * - The loop runs only while the logo is shown (no data-ssc-paused), on screen and the tab is visible.
 * - prefers-reduced-motion: a single still front-facing frame, no loop.
 */
(function () {
  'use strict';

  var FINISH = { color: 0x3a3d3b, metalness: 1, roughness: 0.12 }; // black chrome
  var EXPOSURE = 1.05;
  var CAMERA_Z = 11.5;
  var REST = 1.9;
  var TURN = 1.15;
  var CYCLE = REST + TURN;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var libsPromise = null;
  var geometryPromise = null;

  function ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /** Rest front-facing, then one 360° Y turn (easeInOutCubic), looping */
  function angle(sec) {
    if (reducedMotion.matches) return 0;
    var n = Math.floor(sec / CYCLE);
    var r = sec - n * CYCLE;
    var p = r < REST ? 0 : ease((r - REST) / TURN);
    return (n + p) * Math.PI * 2;
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = resolve;
      script.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(script);
    });
  }

  function loadLibs(root) {
    if (window.THREE && window.THREE.SVGLoader) return Promise.resolve(window.THREE);
    if (!libsPromise) {
      libsPromise = (window.THREE ? Promise.resolve() : loadScript(root.dataset.threeUrl))
        .then(function () { return window.THREE.SVGLoader ? null : loadScript(root.dataset.svgLoaderUrl); })
        .then(function () { return window.THREE; });
    }
    return libsPromise;
  }

  function loadGeometry(THREE, svgUrl) {
    if (!geometryPromise) {
      geometryPromise = fetch(svgUrl)
        .then(function (response) {
          if (!response.ok) throw new Error('SVG ' + response.status);
          return response.text();
        })
        .then(function (svgText) {
          var data = new THREE.SVGLoader().parse(svgText);
          var shapes = [];
          data.paths.forEach(function (path) {
            THREE.SVGLoader.createShapes(path).forEach(function (shape) { shapes.push(shape); });
          });
          var S = 3.1 / 1010; // mark height ≈ 3.1 scene units
          var geo = new THREE.ExtrudeGeometry(shapes, {
            depth: 0.42 / S,
            bevelEnabled: true,
            bevelThickness: 0.12 / S,
            bevelSize: 0.08 / S,
            bevelSegments: 5,
            curveSegments: 10
          });
          geo.scale(S, S, S);
          geo.rotateX(Math.PI); // SVG y points down: rotate, never mirror with a negative scale (inside-out faces)
          geo.center();
          geo.computeVertexNormals();
          return geo;
        });
    }
    return geometryPromise;
  }

  /** Small studio: dark room, softbox panels and a front wall with diagonal streaks (PMREM) */
  function buildEnv(THREE, renderer) {
    var env = new THREE.Scene();
    var room = new THREE.Mesh(new THREE.BoxGeometry(20, 12, 20), new THREE.MeshBasicMaterial({ color: 0x2a2d2b, side: THREE.BackSide }));
    env.add(room);
    var floor = new THREE.Mesh(new THREE.PlaneGeometry(20, 20), new THREE.MeshBasicMaterial({ color: 0x0c0d0c }));
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -5.9;
    env.add(floor);
    function panel(w, h, x, y, z, ry, rx, c) {
      var m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: c, side: THREE.DoubleSide }));
      m.position.set(x, y, z);
      m.rotation.y = ry || 0;
      m.rotation.x = rx || 0;
      env.add(m);
      return m;
    }
    panel(12, 1.4, 0, 5.8, 0, 0, Math.PI / 2, 0xffffff); // top strip
    panel(3, 8, -9.8, 0.5, 2, Math.PI / 2, 0, 0xffffff); // left softbox
    panel(2, 6, 9.8, 1, -3, -Math.PI / 2, 0, 0xdfe8ff); // right cool
    // front wall: diagonal studio streaks, this is what the flat face mirrors at rest
    var c = document.createElement('canvas');
    c.width = 512;
    c.height = 512;
    var g = c.getContext('2d');
    var grad = g.createLinearGradient(150, 150, 362, 362);
    grad.addColorStop(0, '#f4f6f5');
    grad.addColorStop(0.32, '#9ea3a0');
    grad.addColorStop(0.46, '#ffffff');
    grad.addColorStop(0.52, '#3d413f');
    grad.addColorStop(0.7, '#7d827f');
    grad.addColorStop(0.86, '#e8ebe9');
    grad.addColorStop(1, '#4a4e4c');
    g.fillStyle = grad;
    g.fillRect(0, 0, 512, 512);
    var wallTex = new THREE.CanvasTexture(c);
    wallTex.encoding = THREE.sRGBEncoding;
    var wall = panel(20, 12, 0, 0, 9.9, Math.PI, 0, 0xffffff);
    wall.material.map = wallTex;
    panel(4, 3, 4, 2, -9.8, 0, 0, 0xffe7d0); // back warm
    var pm = new THREE.PMREMGenerator(renderer);
    var tex = pm.fromScene(env, 0.03).texture;
    pm.dispose();
    return tex;
  }

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) {
      return false;
    }
  }

  /** The plain mark is hidden while the 3D logo loads (no flat-to-chrome jump on reload); shown only as a fallback */
  function showFallback(root) {
    root.classList.add('is-fallback');
  }

  function initOne(root) {
    if (root.__sscChromeInit) return;
    root.__sscChromeInit = true;
    if (!hasWebGL()) {
      showFallback(root); // keep the plain mark
      return;
    }

    loadLibs(root)
      .then(function (THREE) {
        return loadGeometry(THREE, root.dataset.svgUrl).then(function (geo) { start(THREE, root, geo); });
      })
      .catch(function () {
        root.__sscChromeInit = false; // keep the plain mark; allow a later retry
        showFallback(root);
      });
  }

  function start(THREE, root, geo) {
    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    root.appendChild(canvas);

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    } catch (e) {
      canvas.remove();
      showFallback(root);
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = EXPOSURE;

    var scene = new THREE.Scene();
    scene.environment = buildEnv(THREE, renderer);
    var mat = new THREE.MeshStandardMaterial({ color: FINISH.color, metalness: FINISH.metalness, roughness: FINISH.roughness });
    var mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -0.08;
    scene.add(mesh);
    var cam = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
    cam.position.set(0, 0, CAMERA_Z);

    var onScreen = true;
    var raf = 0;
    var t0 = performance.now();
    var hasRendered = false;

    function size() {
      var w = root.clientWidth;
      var h = root.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
    }

    function shown() {
      return !root.hasAttribute('data-ssc-paused');
    }

    function render(now) {
      mesh.rotation.y = angle((now - t0) / 1000);
      renderer.render(scene, cam);
      if (!hasRendered) {
        hasRendered = true;
        root.classList.add('is-ready');
      }
    }

    function shouldLoop() {
      return root.isConnected && shown() && onScreen && !document.hidden && !reducedMotion.matches;
    }

    function frame(now) {
      raf = 0;
      if (!root.isConnected) {
        dispose();
        return;
      }
      render(now);
      if (shouldLoop()) raf = requestAnimationFrame(frame);
    }

    function update() {
      if (!root.isConnected) {
        dispose();
        return;
      }
      if (reducedMotion.matches) {
        cancelAnimationFrame(raf);
        raf = 0;
        render(t0); // one still front-facing frame
        return;
      }
      if (shouldLoop() && !raf) raf = requestAnimationFrame(frame);
      if (!shouldLoop() && raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    }

    function dispose() {
      cancelAnimationFrame(raf);
      raf = 0;
      intersection.disconnect();
      attributes.disconnect();
      resize.disconnect();
      document.removeEventListener('visibilitychange', update);
      reducedMotion.removeEventListener('change', update);
      renderer.dispose();
    }

    var intersection = new IntersectionObserver(function (entries) {
      onScreen = entries[entries.length - 1].isIntersecting;
      update();
    });
    intersection.observe(root);

    // The header sets data-ssc-paused while the logo is hidden; restart the cycle (rest first) when shown
    var attributes = new MutationObserver(function () {
      if (shown()) t0 = performance.now();
      update();
    });
    attributes.observe(root, { attributes: true, attributeFilter: ['data-ssc-paused'] });

    var resize = new ResizeObserver(function () {
      size();
      if (!raf) render(performance.now());
    });
    resize.observe(root);

    document.addEventListener('visibilitychange', update);
    reducedMotion.addEventListener('change', update);

    size();
    render(t0); // first frame (front-facing): swaps the plain mark for the canvas
    update();
  }

  function initAll() {
    document.querySelectorAll('[data-ssc-chrome-logo]').forEach(initOne);
    // Safety net: if the 3D logo hasn't rendered after 6s (slow network), show the plain mark
    setTimeout(function () {
      document.querySelectorAll('[data-ssc-chrome-logo]:not(.is-ready)').forEach(showFallback);
    }, 6000);
  }

  function whenIdle(callback) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 2000 });
    else setTimeout(callback, 200);
  }

  if (document.readyState === 'complete') whenIdle(initAll);
  else window.addEventListener('load', function () { whenIdle(initAll); });

  window.SSCChromeLogo = { init: initAll };
})();
